// Size & Colour on transaction lines (handover section 6.5).
//
// A variant is one product + size + colour. The product list
// (Product/GetAllByBranch) carries each product's `productVariants[]` with
// their barcodes, so scans and searches resolve locally;
// SizeColour/GetByBarcode is the fallback for a barcode the list doesn't know.
//
// Every line keeps two extra form fields: `variantID` (sent to the server) and
// `variantLabel` ("M · Red", shown under the item code). A line with no
// variant has variantID null - the product itself, as before.
import { getByBarcode } from "../../services/SizeColourApiServices";

const clean = (v) => `${v ?? ""}`.trim();
const low = (v) => clean(v).toLowerCase();

export const variantsOf = (p) =>
  (Array.isArray(p?.productVariants) ? p.productVariants : []).filter((v) => v && v.variantID && v.isActive !== false);

export const variantBarcodes = (v) =>
  (Array.isArray(v?.barcodes) ? v.barcodes : []).map((b) => clean(b?.barcode ?? b)).filter(Boolean);

export const variantLabel = (v) => [v?.sizeCode || v?.sizeName, v?.colourName].filter(Boolean).join(" · ");

// The product as the entry flow sees it, standing for one variant: its own
// name, code (SKU), primary barcode and - when set - its own sales price.
export const asVariantProduct = (p, v) => ({
  ...p,
  __variant: v,
  name: `${p.name} · ${variantLabel(v)}`,
  code: v.sku || p.code,
  barcode: variantBarcodes(v)[0] || "",
  barcodes: variantBarcodes(v),
  retailRate: v.effectiveSalesRate ?? v.salesRate ?? p.retailRate,
});

// a scanned code that is one of a variant's barcodes
export const findVariantByBarcode = (productList, text) => {
  const t = low(text);
  if (!t) return null;
  for (const p of productList || []) {
    const v = variantsOf(p).find((x) => variantBarcodes(x).some((b) => b.toLowerCase() === t) || low(x.sku) === t);
    if (v) return asVariantProduct(p, v);
  }
  return null;
};

// a variant barcode that is longer than the text, so a scanner may still be typing
export const variantBarcodeCouldGrow = (productList, text) => {
  const t = low(text);
  return (productList || []).some((p) =>
    variantsOf(p).some((v) => variantBarcodes(v).some((b) => b.length > t.length && b.toLowerCase().startsWith(t)))
  );
};

// search results: each variant of a product whose name, SKU, size, colour or barcode matches
export const searchVariants = (productList, query, limit = 8) => {
  const q = low(query);
  if (!q) return [];
  const out = [];
  for (const p of productList || []) {
    const vs = variantsOf(p);
    if (!vs.length) continue;
    const nameHit = low(p.name).includes(q) || low(p.code).includes(q);
    for (const v of vs) {
      if (
        nameHit ||
        low(v.sku).includes(q) ||
        low(variantLabel(v)).includes(q) ||
        variantBarcodes(v).some((b) => b.toLowerCase().includes(q))
      )
        out.push(asVariantProduct(p, v));
      if (out.length >= limit) return out;
    }
  }
  return out;
};

// server fallback for a barcode the product list doesn't know; null when none
export const lookupVariantBarcode = async (productList, text) => {
  try {
    const r = await getByBarcode(clean(text));
    if (!r?.productID) return null;
    const p = (productList || []).find((x) => low(x.guid) === low(r.productID));
    if (!p) return null;
    if (!r.variantID) return p;
    const v = variantsOf(p).find((x) => low(x.variantID) === low(r.variantID)) || {
      variantID: r.variantID,
      sku: r.sku,
      sizeCode: r.sizeCode,
      sizeName: r.sizeName,
      colourName: r.colourName,
      hexCode: r.hexCode,
      salesRate: r.salesRate,
      barcodes: [{ barcode: r.barcode }],
    };
    return asVariantProduct(p, v);
  } catch {
    return null; // 404 = no match
  }
};

/* ---------------- the line's form fields ---------------- */
export const setLineVariant = (setValue, index, v) => {
  setValue(`items[${index}].variantID`, v?.variantID || null);
  setValue(`items[${index}].variantLabel`, v ? variantLabel(v) : "");
};

// from a saved document line (the list endpoints return these per line)
export const variantOfDetail = (d) => ({
  variantID: d?.variantID || null,
  variantLabel: d?.variantID ? [d.sizeCode || d.sizeName, d.colourName].filter(Boolean).join(" · ") : "",
});

// the duplicate-line rule: the same product twice is fine when the variants differ
export const lineKey = (item) => `${item?.product ?? ""}|${item?.variantID || ""}`;
export const isDuplicateLine = (items, index) => {
  const k = lineKey(items?.[index]);
  return (items || []).filter((x) => lineKey(x) === k).length > 1;
};

// the small code line under the item name
export const withVariant = (rowCode, label) => [rowCode, label].filter(Boolean).join(" · ");
